import { Component, DestroyRef, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  NbDialogService,
  NbTabComponent,
  NbToastrService,
} from '@nebular/theme';
import { TranslatePipe } from '@ngx-translate/core';
import { first, skip, Subject, takeUntil } from 'rxjs';
import { BucketDialogComponent } from '../../components/dialogs/bucket-dialog/bucket-dialog.component';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { FileUploadDialogComponent } from '../../components/dialogs/file-upload-dialog/file-upload-dialog.component';
import {
  OrganisationFeatures,
  OrganisationService,
} from '../../services/organisation.service';
import { WebsiteDialogComponent } from '../../components/dialogs/website-dialog/website-dialog.component';
import {
  BucketService,
  IBucket,
  isSystemBucket,
  IFile,
  IWebsite,
} from '../../services/bucket.service';
import { AccessService } from '../../services/access.service';
import { AuthService, UserRoles } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { AgentRefreshService } from '../../agent/agent-refresh.service';
import { FileUploadQueueService } from '../../services/file-upload-queue.service';
import { AgentContextService } from '../../agent/agent-context.service';
import { entityRefs } from '../../agent/agent-catalog';

@Component({
  selector: 'app-knowledge-base',
  templateUrl: './knowledge-base.component.html',
  styleUrl: './knowledge-base.component.scss',
})
export class KnowledgeBaseComponent implements OnDestroy {
  selectedBucket: IBucket | undefined = undefined;
  currentTab: string = 'Datein';
  loading: boolean = false;
  searchText = '';
  rightSearchText = '';
  displayedBuckets: IBucket[] = [];
  pagedBuckets: IBucket[] = [];
  bucketPage = 0;
  bucketPageSize = 25;
  readonly bucketPageSizeOptions = [25, 50, 100];
  private allBuckets: IBucket[] = [];

  /** "Filter by user access" state — admin/manager only. `selectedUserFilter`
   *  'all' shows every bucket; a user id narrows to what that user may see
   *  (owned + granted), fetched into `accessibleBucketIds`. */
  selectedUserFilter = 'all';
  users: { id: string; name: string }[] = [];
  private accessibleBucketIds: Set<string> | null = null;

  private static readonly BASE_REFRESH_MS = 5000;
  private readonly destroy$ = new Subject<void>();
  private entriesRefreshTimer?: ReturnType<typeof setTimeout>;
  private entriesRefreshDelayMs = KnowledgeBaseComponent.BASE_REFRESH_MS;

  constructor(
    protected bucketService: BucketService,
    protected dialogService: NbDialogService,
    protected nbToastrService: NbToastrService,
    protected t: TranslatePipe,
    protected authService: AuthService,
    protected accessService: AccessService,
    protected userService: UserService,
    private router: Router,
    private organisationService: OrganisationService,
  ) {
    this.loadUsers();
  }

  /** Der Hinweis unter dem Titel verweist auf „Platzierungen" als den Weg,
   *  Kursinhalte in die Wissensbasis zu bringen — aber nur, wenn die
   *  Organisation den Menüpunkt hat (Funktion TUTOR_PLACEMENTS, wie in der
   *  Menüleiste). Sonst steht dort nur, wofür diese Seite da ist. */
  get hasPlacements(): boolean {
    return this.organisationService.hasFeature(OrganisationFeatures.TUTOR_PLACEMENTS);
  }

  get canFilterByUser(): boolean {
    const role = this.authService.accessTokenPayload$.value?.role;
    return (
      role === UserRoles.Admin ||
      role === UserRoles.SuperAdmin ||
      role === UserRoles.Manager
    );
  }

  private loadUsers(): void {
    if (!this.canFilterByUser) return;
    this.userService.listUsers().subscribe((list) => {
      this.users = list
        .map((u) => ({ id: u.id, name: u.name || u.email }))
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  async onUserFilterChange(userId: string): Promise<void> {
    this.selectedUserFilter = userId;
    if (userId === 'all') {
      this.accessibleBucketIds = null;
    } else {
      try {
        this.accessibleBucketIds = new Set(
          await this.accessService.getUserBucketIds(userId),
        );
      } catch {
        this.accessibleBucketIds = new Set();
      }
    }
    this.onSearchChange();
  }

  onSearchChange() {
    const q = this.searchText.toLowerCase();
    const byUser = this.accessibleBucketIds;
    this.displayedBuckets = this.allBuckets.filter(
      (b) =>
        (!byUser || byUser.has(b.id)) &&
        (!q || b.name.toLowerCase().includes(q)),
    );
    this.bucketPage = 0;
    this.updatePagedBuckets();
  }

  onBucketPageChange(page: number): void {
    this.bucketPage = page;
    this.updatePagedBuckets();
  }

  onBucketPageSizeChange(size: number): void {
    this.bucketPageSize = Math.min(Math.max(size, 1), 100);
    this.bucketPage = 0;
    this.updatePagedBuckets();
  }

  private updatePagedBuckets(): void {
    const maxPage = Math.max(Math.ceil(this.displayedBuckets.length / this.bucketPageSize) - 1, 0);
    this.bucketPage = Math.max(0, Math.min(this.bucketPage, maxPage));
    const start = this.bucketPage * this.bucketPageSize;
    this.pagedBuckets = this.displayedBuckets.slice(start, start + this.bucketPageSize);
  }

  private readonly agentRefresh = inject(AgentRefreshService);
  private readonly agentContext = inject(AgentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly uploadQueue = inject(FileUploadQueueService);

  ngOnInit(): void {
    this.bucketService.listBuckets();

    // "this knowledge base" for the in-house agent = the one open on the left.
    this.agentContext.report(
      this,
      () => ({ selection: entityRefs('bucket', [this.selectedBucket?.id]) }),
      this.destroyRef
    );

    this.agentRefresh
      .on('buckets')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.bucketService.listBuckets());
    this.agentRefresh
      .on('files')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.selectedBucket?.id) this.bucketService.getBucketEntries(this.selectedBucket.id).subscribe();
      });
    // Uploads laufen im Hintergrund weiter (FileUploadQueueService) — die
    // Liste nur neu laden, wenn der fertige Durchgang den offenen Bucket betraf.
    this.uploadQueue.batchDone$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ bucketId }) => {
        if (bucketId === this.selectedBucket?.id) this.bucketService.getBucketEntries(bucketId).subscribe();
      });

    this.bucketService._buckets
      .pipe(takeUntil(this.destroy$))
      .subscribe((buckets) => {
        // Internal `__…` folders (one `__dozent_knowledge__` per lecturer)
        // are not the customer's — the lecturer manages theirs under
        // Dozent › FAQ / Wissen › Dateien (#230, rule in `isSystemBucket`).
        this.allBuckets = (buckets ?? []).filter((b) => !isSystemBucket(b));
        this.onSearchChange();
      });

    this.bucketService._buckets.pipe(skip(1), first()).subscribe((buckets) => {
      // when there is bucketId in url query, select the bucket
      const bucketId = this.router.parseUrl(this.router.url).queryParams[
        'bucketId'
      ];
      const visible = buckets.filter((b) => !isSystemBucket(b));
      const bucket = visible.find((b) => b.id === bucketId);

      if (bucketId && bucket) {
        this.selectedBucket = bucket;
        this.onSelect(this.selectedBucket);
      } else {
        this.onSelect(visible[0]);
      }
    });

    this.bucketService._websites
      .pipe(takeUntil(this.destroy$))
      .subscribe((websites) => {
        const isProcessing = websites.some((web) => {
          if (web.status != 'ACTIVE') {
            return true;
          }
          return false;
        });

        if (isProcessing) {
          this.scheduleEntriesRefresh();
        } else {
          this.entriesRefreshDelayMs = KnowledgeBaseComponent.BASE_REFRESH_MS;
        }
      });
  }

  /** Refresh entries while a website is still processing — single-flight:
   *  at most one pending timer, no matter how often _websites emits. On
   *  errors the delay backs off exponentially (max 60s) so a struggling
   *  backend is not hammered by every open tab (caused the 2026-07-24
   *  al-filemanager kill cascade). */
  private scheduleEntriesRefresh(): void {
    if (this.entriesRefreshTimer !== undefined) return;
    const previousBucket = this.selectedBucket;
    this.entriesRefreshTimer = setTimeout(() => {
      this.entriesRefreshTimer = undefined;
      if (!previousBucket?.id || this.selectedBucket?.id !== previousBucket.id)
        return;
      this.bucketService.getBucketEntries(previousBucket.id).subscribe({
        next: () => {
          this.entriesRefreshDelayMs = KnowledgeBaseComponent.BASE_REFRESH_MS;
        },
        error: () => {
          this.entriesRefreshDelayMs = Math.min(
            this.entriesRefreshDelayMs * 2,
            60000
          );
          this.scheduleEntriesRefresh();
        },
      });
    }, this.entriesRefreshDelayMs);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.entriesRefreshTimer !== undefined) {
      clearTimeout(this.entriesRefreshTimer);
      this.entriesRefreshTimer = undefined;
    }
  }

  onAdd(bucket: IBucket | undefined = undefined) {
    const dialogRef = this.dialogService.open(BucketDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        data: bucket,
        onComplete: (bucket: IBucket) => {
          if (bucket.id) {
            this.onSelect(bucket);
          }
        },
      },
    });

    // Fetch the updated projects list after the dialog is closed
    dialogRef.onClose.subscribe(() => {
      // this.filesService.listBuckets();
    });
  }

  onDelete(bucket: IBucket, event: MouseEvent) {
    event.stopPropagation();
    const confirmDialogRef = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        onConfirm: async () => {
          await this.bucketService.deleteBucket(bucket);
          if (bucket.id === this.selectedBucket?.id) {
            this.selectedBucket = this.bucketService._buckets.value?.[0];
          }
          confirmDialogRef.close();
        },
      },
    });
  }

  onSelect(bucket: IBucket) {
    if (!bucket?.id) {
      this.selectedBucket = undefined;
      return;
    }

    this.selectedBucket = bucket;
    // add bucket id to url query
    this.router.navigate([], {
      queryParams: { bucketId: bucket.id },
      queryParamsHandling: 'merge',
    });

    this.loading = true;
    this.bucketService.getBucketEntries(bucket.id!).subscribe((entries) => {
      console.log('Bucket Entries: ', entries);
      this.loading = false;
    });
  }

  onFileUpload() {
    const dialogRef = this.dialogService.open(FileUploadDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        bucketId: this.selectedBucket?.id,
      },
    });
  }

  onWebsiteAdd() {
    const dialogRef = this.dialogService.open(WebsiteDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        bucketId: this.selectedBucket?.id!,
      },
    });
  }

  onDeleteFile(file: IFile) {
    const confirmDialogRef = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        onConfirm: async () => {
          await this.bucketService.deleteFile(file);
          confirmDialogRef.close();
        },
      },
    });
  }

  onChangeTab(event: NbTabComponent) {
    this.currentTab = event.tabTitle;
  }

  onWebsiteRefresh(web: IWebsite) {
    this.loading = true;
    this.bucketService.refreshWebsite([web.datatype_id]).subscribe(() => {
      this.loading = false;
      this.nbToastrService.success('Website wird in Datenbank aktualisiert.');
    });
  }
  onDeleteWebsite(web: IWebsite) {
    const confirmDialogRef = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        onConfirm: () => {
          this.bucketService.deleteDatatype([web.datatype_id]).subscribe(() => {
            confirmDialogRef.close();
            this.bucketService
              .getBucketEntries(this.selectedBucket!.id!)
              .subscribe();
          });
        },
      },
    });
  }
}
